"use client";

import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { chartPointCategorySummary } from "./record-dashboard-chart-summary";
import {
  compactNumber,
  foldChartPoints,
  type ChartPoint,
  type MetricDisplayKind,
} from "./record-dashboard-model";
import {
  DASHBOARD_CHART_FRAME,
  DASHBOARD_SERIES_COLOR,
  DashboardChart,
  DashboardLegend,
  DashboardTooltip,
  dashboardSeriesColor,
} from "./record-dashboard-shell";
import {
  axisTickKind,
  positiveTopDomain,
} from "./record-dashboard-series-chart";

const BAR_GAIN_COLOR = "hsl(var(--success))";
const BAR_LOSS_COLOR = "hsl(var(--destructive))";

export function DashboardCategoryChart({
  title,
  data,
  displayKind,
  seriesName = "Value",
  additive = true,
  integerTicks = false,
  onPointSelect,
}: {
  title: string;
  data: ChartPoint[];
  displayKind: MetricDisplayKind;
  seriesName?: string;
  additive?: boolean;
  /** Count axes never show fractional ticks. */
  integerTicks?: boolean;
  onPointSelect?: (label: string) => void;
}) {
  const diverging = data.some((point) => point.value < 0);
  return (
    <DashboardChart
      label={title}
      clickable={Boolean(onPointSelect)}
      summary={chartPointCategorySummary(title, data, displayKind, additive)}
    >
      <ResponsiveContainer
        width="100%"
        height="100%"
        initialDimension={{ width: 420, height: 220 }}
      >
        <BarChart
          accessibilityLayer
          data={data}
          margin={DASHBOARD_CHART_FRAME.margin}
          barCategoryGap="26%"
        >
          <CartesianGrid
            vertical={false}
            stroke="hsl(var(--border))"
            strokeOpacity={0.58}
          />
          <XAxis
            dataKey="label"
            tickLine={false}
            axisLine={false}
            tickMargin={9}
            minTickGap={14}
            interval={data.length <= 8 ? 0 : undefined}
            tickFormatter={compactCategoryLabel}
          />
          <YAxis
            domain={diverging ? undefined : positiveTopDomain(1.12)}
            allowDecimals={!integerTicks}
            tickLine={false}
            axisLine={false}
            tickMargin={7}
            width={DASHBOARD_CHART_FRAME.yAxisWidth}
            tickFormatter={(value) =>
              compactNumber(Number(value), axisTickKind(displayKind))
            }
          />
          <Tooltip
            cursor={{ fill: "hsl(var(--muted))", fillOpacity: 0.35 }}
            content={<DashboardTooltip displayKind={displayKind} />}
          />
          {diverging ? (
            <ReferenceLine
              y={0}
              stroke="hsl(var(--muted-foreground))"
              strokeOpacity={0.5}
            />
          ) : null}
          <Bar
            dataKey="value"
            name={seriesName}
            radius={diverging ? [3, 3, 3, 3] : DASHBOARD_CHART_FRAME.barRadius}
            maxBarSize={DASHBOARD_CHART_FRAME.maxBarSize}
            isAnimationActive={false}
            onClick={
              onPointSelect
                ? (item) => {
                    // BarRectangleItem carries the datum under payload.
                    const label = (item as { payload?: { label?: unknown } })
                      ?.payload?.label;
                    if (typeof label === "string") onPointSelect(label);
                  }
                : undefined
            }
          >
            {data.map((point) => (
              <Cell
                key={point.label}
                fill={
                  diverging
                    ? point.value >= 0
                      ? BAR_GAIN_COLOR
                      : BAR_LOSS_COLOR
                    : DASHBOARD_SERIES_COLOR
                }
                fillOpacity={diverging ? 0.9 : 0.86}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </DashboardChart>
  );
}

export function DashboardRadialChart({
  title,
  data,
  displayKind = "number",
  variant = "donut",
  additive = true,
  onPointSelect,
}: {
  title: string;
  data: ChartPoint[];
  displayKind?: MetricDisplayKind;
  variant?: "pie" | "donut";
  additive?: boolean;
  onPointSelect?: (label: string) => void;
}) {
  // Folding the tail into "Other" only makes sense for additive metrics;
  // non-additive slices keep their own (overflow-coloured) identity.
  const points = additive ? foldChartPoints(data) : data;
  const total = data.reduce((sum, point) => sum + point.value, 0);
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <DashboardChart
        label={title}
        clickable={Boolean(onPointSelect)}
        summary={chartPointCategorySummary(
          title,
          points,
          displayKind,
          additive,
        )}
      >
        <ResponsiveContainer
          width="100%"
          height="100%"
          initialDimension={{ width: 320, height: 180 }}
        >
          <PieChart accessibilityLayer>
            <Tooltip
              content={
                <DashboardTooltip
                  displayKind={displayKind}
                  shareOfTotal={additive && total > 0 ? total : undefined}
                />
              }
            />
            <Pie
              data={points}
              dataKey="value"
              nameKey="label"
              onClick={
                onPointSelect
                  ? (item) => {
                      // nameKey="label": the sector's name is the category label.
                      if (typeof item?.name === "string")
                        onPointSelect(item.name);
                    }
                  : undefined
              }
              innerRadius={variant === "donut" ? "54%" : 0}
              outerRadius="78%"
              paddingAngle={variant === "donut" ? 2 : 1}
              cornerRadius={variant === "donut" ? 3 : 1}
              label={({ x, y, cx, index }) => (
                <text
                  x={x}
                  y={y}
                  textAnchor={x > cx ? "start" : "end"}
                  fill="hsl(var(--foreground))"
                  fontSize={11}
                >
                  {index + 1}
                </text>
              )}
              stroke="hsl(var(--surface))"
              strokeWidth={2}
              isAnimationActive={false}
            >
              {points.map((point, index) => (
                <Cell key={point.label} fill={dashboardSeriesColor(index)} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        {variant === "donut" && additive ? (
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-lg font-semibold leading-none tracking-[-0.025em] text-foreground tabular-nums">
              {compactNumber(total, displayKind)}
            </span>
            <span className="mt-1 text-3xs font-medium uppercase tracking-[0.1em] text-muted-foreground">
              Total
            </span>
          </div>
        ) : null}
      </DashboardChart>
      <DashboardLegend
        items={points.map((point, index) => ({
          key: point.label,
          label: `${index + 1}. ${point.label}`,
          color: dashboardSeriesColor(index),
        }))}
      />
    </div>
  );
}

export function compactCategoryLabel(value: string | number): string {
  const label = String(value);
  return label.length > 14 ? `${label.slice(0, 13)}…` : label;
}
